<template>
  <div
    class="rs-form-importer-field"
    :class="`${isFlexStyle ? 'flex-style' : ''}`"
  >
    <div
      @focusin="handleSlotFocusin"
      @focusout="handleSlotFocusout"
    >
      <slot
        :input-id="fieldInputId"
        :grayout="fieldGrayout"
      />
      <div
        :class="{ 'lh-80': relativeHelpText }"
      >
        <span
          v-if="!hideBanner && (isEditing || displayBanner) && !!dataSource"
          class="warning-banner"
          :class="`${customClasses} ${relativeHelpText ? 'p-relative' : ''}`"
          :style="`bottom:${bottomOffset}px;`"
        >
          Changes made will be temporary.
        </span>
      </div>
    </div>

    <img
      v-if="bstkImportConfig?.field && showIcon"
      :id="`${id}-icon`"
      src="/img/bstk/bstk-logo-sm.png"
      :class="{
        'importer-icon': true,
        inline: inline,
        editing: isEditing,
      }"
    />

    <i
      v-if="!bstkImportConfig?.field && showIcon && dataSource?.length"
      :id="`${id}-icon`"
      :class="[
        'fa-solid', 'fa-hexagon-exclamation', 'importer-icon',
        { inline: inline, editing: isEditing, 'importer-icon-fixed': useCustomIconPosition },
      ]"
    />
    <RsTooltip
      v-if="showIcon && (dataSource?.length || bstkImportConfig?.field)"
      :target="`${id}-icon`"
      :title="dataSourceMessage"
      :delay="{ show: 200 }"
    />
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { TBSTKImportConfig } from '@/shared/helpers/building-stack';

/**
 * An Example Here:
 * <RsFormImporterField
    id="promotion-upload-image"
    v-slot="{ inputId }"   (The inputId must be consistent)
    :data-source="fieldSource['promotions_image']"
    :hide-banner="true"
  >
    <RsFormFile
      :id="inputId" (The inputId must be consistent)
      variant="bootstrap"
      :multiple="false"
      prompt="Choose Image"
      label="Upload Image"
      :current-file-name="$filters.rsFilename(promotionToEdit.image)"
      @on-upload-triggered="updateImage"
      @on-file-reset="clearFiles('image')"
    />
  </RsFormImporterField>
 */
export default defineComponent({
  name: 'RsFormImporterField',

  props: {
    id: String,

    dataSource: String,

    inline: {
      type: Boolean,
      default: false,
    },

    displayBanner: {
      type: Boolean,
      default: false,
    },

    hideBanner: {
      type: Boolean,
      default: false,
    },

    isFlexStyle: {
      type: Boolean,
      default: false,
    },

    bottomOffset: {
      type: Number,
      default: -7,
    },

    showIcon: {
      type: Boolean,
      default: true,
    },

    relativeHelpText: {
      type: Boolean,
      default: false,
    },

    iconOffsetX: {
      type: Number,
      default: null,
    },

    iconOffsetY: {
      type: Number,
      default: null,
    },

    customClasses: {
      type: String,
      default: '',
    },

    bstkImportConfig: {
      type: Object as PropType<TBSTKImportConfig | null>,
      default: () => null,
    },

    /**
     * Used to indicate a specific input type for a field.
     * This is useful for cases where the input element has a specific ID assigned by the DOM.
     */
    domInputType: {
      type: String,
      default: '',
    },
    /**
     * When true, applies custom positioning to prevent icon movement
     */
    useCustomIconPosition: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isEditing: false,
    };
  },

  computed: {
    fieldInputId(): string {
      return `${this.id}-target`;
    },

    fieldGrayout(): boolean {
      return (!!this.dataSource || !!this.bstkImportConfig?.field) && !this.isEditing;
    },

    /**
     * Will return a message based on where this is a traditional data source or a BSTK one.
     */
    dataSourceMessage() {
      if (this.bstkImportConfig?.field) {
        return `This field is managed by your Building Stack integration. Changes made will be temporary.`;
      }

      return `This field is managed by ${this.dataSource}. Changes made will be temporary.`;
    },
  },

  watch: {
    fieldGrayout: {
      immediate: true,
      handler() {
        const inputEl = this.getInputElement();
        if (this.fieldGrayout && inputEl) {
          inputEl.classList.add('disabled');
        }
      },
    },
  },

  mounted() {
    setTimeout(() => {
      this.setElementStates();
    }, 200);
    this.$nextTick(() => {
      const tryApplyStyles = () => {
        const inputEl = this.getInputElement();
        const iconEl = document.getElementById(`${this.id}-icon`);

        if (this.fieldGrayout && inputEl) {
          inputEl.classList.add('disabled');
        }

        if (iconEl) {
          if (this.iconOffsetX !== null) {
            iconEl.style.right = `${this.iconOffsetX}px`;
          }
          if (this.iconOffsetY !== null) {
            iconEl.style.top = `${this.iconOffsetY}px`;
          }
          return true;
        }

        return false;
      };

      // Keep trying until DOM elements exist or timeout after ~500ms
      let attempts = 0;
      const maxAttempts = 100;
      const interval = setInterval(() => {
        if (tryApplyStyles() || ++attempts >= maxAttempts) {
          clearInterval(interval);
        }
      }, 50);
    });
  },

  methods: {
    /**
     * Retrieves the input DOM element based on the domInputType and fieldInputId.
     * Easily extendable for additional input types.
     */
    getInputElement() {
      const id = this?.fieldInputId;
      if (!id) return null;

      const inputIdMap: Record<string, string> = {
        switch: `switch_${id}`,
        spin: `${id}-field`,
        // Add other input types here with their specific DOM ids
      };

      const elementId: string = inputIdMap[this.domInputType] || id;
      return document.getElementById(elementId);
    },

    getIconElement() {
      return document.getElementById(`${this.id}-icon`);
    },

    setElementStates() {
      const [ iconEl, inputEl ] = [ this.getIconElement(), this.getInputElement() ];

      if (!inputEl) {
        return;
      }

      if (this.fieldGrayout) {
        inputEl.classList.add('disabled');
      }

      if (!iconEl) {
        return;
      }

      if (this.iconOffsetX !== null) {
        iconEl.style.setProperty('right', `${this.iconOffsetX}px`);
      }

      if (this.iconOffsetY !== null) {
        iconEl.style.setProperty('top', `${this.iconOffsetY}px`);
      }
    },

    handleSlotFocusin() {
      this.isEditing = true;
      const ie = this.getInputElement();
      if (ie) {
        ie.classList.remove('disabled');
      }
    },

    handleSlotFocusout(event: Event) {
      this.isEditing = false;
    },
  },
});
</script>
<style lang="scss">
@import '@/styles/_variables.scss';

.rs-form-importer-field {
  .flex-style {
    display: flex;
    position: absolute;

    .importer-icon {
      right: 7px;
    }
  }

  .importer-icon {
    width: 14px !important;
    height: 14px !important;
    position: absolute;
    top: 7px;
    right: 14px;
    color: $tertiary-muted;
    z-index: 100;

    &:hover, &.editing {
      color: $error;
    }

    &.inline {
      top: 3px;
    }
  }

  img.importer-icon {
    width: 16px !important;
    height: 16px !important;
  }

  &.importer-icon-fixed {
    position: relative;
    transform: translateX(-100%);
  }
}
.disabled {
  color: #6c757d !important;
  background-color: #e9ecef !important;
  border-color: #c9ccdf !important;

  &.importer-icon-fixed {
    position: relative;
    transform: translateX(-100%);
  }

  input, button, + label,
  .minus-input-container,
  .plus-input-container,
  .number-input-container,
  .number-input {
    color: #6c757d !important;
    background-color: #e9ecef !important;
    border-color: #c9ccdf !important;
    input, button, + label {
      color: #6c757d !important;
      background-color: #e9ecef !important;
      border-color: #c9ccdf !important;
    }
  }

  .warning-banner {
    position: absolute;
    bottom: -5px !important;
    width: max-content;
    font-size: 0.8rem;;
    &.p-relative {
      position: relative;
    }
  }

  .lh-80 {
    line-height: 80%;
  }
}
</style>
